import React, { useContext } from 'react';

import { AppContext } from '@prisjakt/ui-app-context';
import ArticleCard from '../../../../components/article-card';
import CardImage from '../card-image';
import ProductImages from '../../toplists/components/product-images';

import { compareByDate } from '../../utils';

import { ExpertItems, Guide, Test, Toplist } from './types';
import { ContentTypes } from '../../types';
import { AdvizedCategory, SearchArticleItem, ToplistHeadline } from 'src/types';
import { Badge } from '../badge';
import { ExpertUserCard } from '../expert-user-card';
import { trackCategoryPageExpertCarouselCard } from '../../../../components/advized/tracking';
import * as S from './styles';

export enum TrackingSource {
  ADVIZED_WIDGET = 'advizedWidget',
}

type Props = {
  className?: string;
  onClick?: (onClickData: ClickInfo) => void;
  items: ExpertItems;
  imageSize?: number;
  limit?: number;
  offset?: number;
  hideExperts?: boolean;
  category?: AdvizedCategory;
  trackingSource?: TrackingSource;
};

type ClickInfo = {
  id: string;
  title: string;
  type: ContentTypes;
  url: string;
  index: number;
};

export default function ContentCards({
  category,
  className,
  onClick,
  items,
  imageSize = 130,
  limit = 0,
  offset = 0,
  hideExperts,
  trackingSource,
}: Props) {
  const { market, t } = useContext(AppContext);

  const numberOfCards = limit ? limit + offset : items.length;

  const getDefaultExpert = () => ({
    avatar: `${market.cdn}/g/rfe/logos/logo_v2_symbol.svg`,
    name: t('meta', 'site.name'),
    url: undefined,
    hideExpertIcon: true,
  });

  const onClickAndTracking = (clickInfo: ClickInfo) => {
    onClick?.(clickInfo);

    switch (trackingSource) {
      case TrackingSource.ADVIZED_WIDGET: {
        trackCategoryPageExpertCarouselCard(category?.url, clickInfo.type);
        break;
      }
      default:
    }
  };

  const getContentCard = (
    item: Toplist | ToplistHeadline | Test | Guide | SearchArticleItem,
    index: number
  ) => {
    switch (item.__typename) {
      case ContentTypes.TOPLIST_HEADLINE: {
        if (!item.toplists || !item.toplists[0]) {
          return null;
        }
        return (
          <ArticleCard
            category={category}
            className={className}
            date={item.toplists[0].date}
            onClick={() =>
              onClickAndTracking({
                id: item.id,
                title: item.name,
                type: ContentTypes.TOPLIST_HEADLINE,
                url: item.url,
                index,
              })
            }
            key={item.id}
            likes={item.toplists[0].headlineInfo?.likes}
            media={
              <ProductImages
                height={`${imageSize}px`}
                background={false}
                selectedPosition={2}
                products={item.toplists[0]?.products?.map(p => ({
                  ...p,
                }))}
              />
            }
            titleAndCategory={
              <>
                <Badge type={ContentTypes.TOPLIST_HEADLINE} />: {item.name}
              </>
            }
            url={item.url}
            footer={
              !hideExperts && item.toplists[0].expert.url ? (
                <ExpertUserCard
                  experts={[
                    item.toplists[0].expert,
                    ...(item.toplists[0]?.headlineInfo?.otherExperts || []),
                  ]}
                />
              ) : null
            }
          />
        );
      }

      case ContentTypes.TOPLIST: {
        return (
          <ArticleCard
            category={category}
            className={className}
            date={item.date}
            onClick={() =>
              onClickAndTracking({
                id: item.id,
                title: item.title,
                type: ContentTypes.TOPLIST,
                url: item.headlineInfo.url,
                index,
              })
            }
            key={item.id}
            likes={item.headlineInfo?.likes}
            media={
              <ProductImages
                height={`${imageSize}px`}
                products={item.products.map(p => ({ ...p }))}
                background={false}
              />
            }
            titleAndCategory={
              <>
                <Badge type={ContentTypes.TOPLIST} />: {item.title}
              </>
            }
            url={item.headlineInfo.url}
            footer={
              !hideExperts && item.expert?.url ? (
                <ExpertUserCard
                  experts={[
                    item.expert,
                    ...(item.headlineInfo?.otherExperts || []),
                  ]}
                />
              ) : null
            }
          />
        );
      }

      case ContentTypes.TEST: {
        return (
          <ArticleCard
            category={category}
            className={className}
            date={item.date}
            grade={getGradeComponent(item.grade)}
            onClick={() =>
              onClickAndTracking({
                id: item.id,
                title: item.title,
                type: ContentTypes.TEST,
                url: item.url,
                index,
              })
            }
            key={item.id}
            likes={item.likes}
            media={
              item.imageWithSize && (
                <CardImage
                  src={item.imageWithSize.src}
                  alt={item.imageWithSize.alt}
                  height={imageSize}
                />
              )
            }
            titleAndCategory={
              <>
                <Badge type={ContentTypes.TEST} />: {item.title}
              </>
            }
            url={item.url}
            footer={
              !hideExperts ? <ExpertUserCard experts={[item.expert]} /> : null
            }
          />
        );
      }

      case ContentTypes.GUIDE: {
        return (
          <ArticleCard
            category={category}
            className={className}
            date={item.date}
            onClick={() =>
              onClickAndTracking({
                id: item.id,
                title: item.title,
                type: ContentTypes.GUIDE,
                url: item.url,
                index,
              })
            }
            key={item.id}
            likes={item.likes}
            media={
              item.image && (
                <CardImage
                  src={item.image.src}
                  alt={item.image.alt}
                  height={imageSize}
                />
              )
            }
            titleAndCategory={
              <>
                <Badge type={ContentTypes.GUIDE} />: {item.title}
              </>
            }
            url={item.url}
            footer={
              !hideExperts ? <ExpertUserCard experts={[item.expert]} /> : null
            }
          />
        );
      }

      case ContentTypes.ARTICLE: {
        return (
          <ArticleCard
            category={category}
            className={className}
            date={item.date}
            onClick={() =>
              onClickAndTracking({
                id: item.id,
                title: item.title,
                type: ContentTypes.ARTICLE,
                url: item.link,
                index,
              })
            }
            key={item.id}
            media={
              item.image && (
                <CardImage
                  src={item.image.src}
                  alt={item.image.alt}
                  height={imageSize}
                />
              )
            }
            titleAndCategory={<>{item.title}</>}
            url={item.link}
            footer={<ExpertUserCard pointer experts={[getDefaultExpert()]} />}
            footerPartOfLink
          />
        );
      }

      default:
        return null;
    }
  };

  return (
    <>
      {items.sort(compareByDate).map((item, index) => {
        if (!item || !item.id || !item.__typename) {
          return null;
        }

        if (index < offset || index >= numberOfCards) {
          return null;
        }

        return getContentCard(item, index);
      })}
    </>
  );
}

const getGradeComponent = (grade: number | undefined) => {
  return grade !== undefined ? <S.Rating grade={grade} /> : undefined;
};
